import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail } from 'lucide-react';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from './ui/sheet';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Projects', path: '/projects' },
    { name: 'HSE & CSR', path: '/hse-csr' },
    { name: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* Top Bar with Marquee */}
      <div className="hidden md:block bg-[#0A0A0A] text-white py-2 overflow-hidden">
        <div className="marquee-container">
          <div className="marquee-content">
            <div className="flex items-center gap-12 text-sm">
              <a href="tel:+447915697011" className="flex items-center gap-2 hover:text-[#FF6B00] transition-colors whitespace-nowrap">
                <Phone size={14} />
                <span>+44 7915 697011</span>
              </a>
              <a href="mailto:admin@godstonprojects.org" className="flex items-center gap-2 hover:text-[#FF6B00] transition-colors whitespace-nowrap">
                <Mail size={14} />
                <span>admin@godstonprojects.org</span>
              </a>
              <a href="mailto:epetegp@godstonprojects.org" className="flex items-center gap-2 hover:text-[#FF6B00] transition-colors whitespace-nowrap">
                <Mail size={14} />
                <span>epetegp@godstonprojects.org</span>
              </a>
              <span className="text-[#FF6B00] whitespace-nowrap">★ Engineering Excellence Since 2008 ★</span>
              <span className="text-slate-400 whitespace-nowrap">Oil & Gas Services | Marine & Logistics | Construction | Procurement</span>
              <a href="tel:+2348107349363" className="flex items-center gap-2 hover:text-[#FF6B00] transition-colors whitespace-nowrap">
                <Phone size={14} />
                <span>+234 810 734 9363</span>
              </a>
              <span className="text-slate-400 whitespace-nowrap">UK & Nigeria Operations</span>
            </div>
            <div className="flex items-center gap-12 text-sm">
              <a href="tel:+447915697011" className="flex items-center gap-2 hover:text-[#FF6B00] transition-colors whitespace-nowrap">
                <Phone size={14} />
                <span>+44 7915 697011</span>
              </a>
              <a href="mailto:admin@godstonprojects.org" className="flex items-center gap-2 hover:text-[#FF6B00] transition-colors whitespace-nowrap">
                <Mail size={14} />
                <span>admin@godstonprojects.org</span>
              </a>
              <a href="mailto:epetegp@godstonprojects.org" className="flex items-center gap-2 hover:text-[#FF6B00] transition-colors whitespace-nowrap">
                <Mail size={14} />
                <span>epetegp@godstonprojects.org</span>
              </a>
              <span className="text-[#FF6B00] whitespace-nowrap">★ Engineering Excellence Since 2008 ★</span>
              <span className="text-slate-400 whitespace-nowrap">Oil & Gas Services | Marine & Logistics | Construction | Procurement</span>
              <a href="tel:+2348107349363" className="flex items-center gap-2 hover:text-[#FF6B00] transition-colors whitespace-nowrap">
                <Phone size={14} />
                <span>+234 810 734 9363</span>
              </a>
              <span className="text-slate-400 whitespace-nowrap">UK & Nigeria Operations</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation - Fixed */}
      <header 
        className="sticky top-0 left-0 right-0 z-50 bg-white shadow-sm"
        data-testid="main-header"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
              <img 
                src="https://customer-assets.emergentagent.com/job_godston-preview/artifacts/7ulqxgtg_WhatsApp%20Image%202026-03-18%20at%2017.43.02.jpeg" 
                alt="Godston Projects Limited Logo"
                className="h-12 w-auto object-contain"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center">
              <div className="flex items-center gap-10">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`relative font-medium text-sm uppercase tracking-wider transition-colors ${
                      isActive(link.path)
                        ? 'text-[#FF6B00]'
                        : 'text-[#0A0A0A] hover:text-[#FF6B00]'
                    }`}
                  >
                    {link.name}
                    {isActive(link.path) && (
                      <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-[#FF6B00]" />
                    )}
                  </Link>
                ))}
              </div>
            </nav>

            {/* CTA Button */}
            <div className="hidden lg:block">
              <Link to="/contact">
                <Button 
                  data-testid="header-cta-button"
                  className="bg-[#FF6B00] text-white hover:bg-[#E65000] rounded-none uppercase tracking-wider font-bold px-6 py-5 btn-glow"
                >
                  Get a Quote
                </Button>
              </Link>
            </div>

            {/* Mobile Menu */}
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="ghost" size="icon" data-testid="mobile-menu-toggle">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] bg-[#0A0A0A] border-none p-0">
                <VisuallyHidden>
                  <SheetTitle>Navigation Menu</SheetTitle>
                  <SheetDescription>Main navigation menu for Godston Projects Limited website</SheetDescription>
                </VisuallyHidden>
                <div className="flex flex-col h-full">
                  <div className="p-6 border-b border-slate-800">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_godston-preview/artifacts/7ulqxgtg_WhatsApp%20Image%202026-03-18%20at%2017.43.02.jpeg" 
                      alt="Godston Projects Limited Logo"
                      className="h-16 w-auto object-contain"
                    />
                  </div>
                  <nav className="flex-1 p-6">
                    <ul className="space-y-4">
                      {navLinks.map((link) => (
                        <li key={link.path}>
                          <Link
                            to={link.path}
                            onClick={() => setIsOpen(false)}
                            data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                            className={`block py-3 text-lg font-medium uppercase tracking-wider transition-colors ${
                              isActive(link.path)
                                ? 'text-[#FF6B00]'
                                : 'text-white hover:text-[#FF6B00]'
                            }`}
                          >
                            {link.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </nav>
                  <div className="p-6 border-t border-slate-800">
                    <Link to="/contact" onClick={() => setIsOpen(false)}>
                      <Button 
                        className="w-full bg-[#FF6B00] text-white hover:bg-[#E65000] rounded-none uppercase tracking-wider font-bold py-4"
                        data-testid="mobile-cta-button"
                      >
                        Get a Quote
                      </Button>
                    </Link>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>
    </>
  );
};

export default Header;
